import React, { useState, useEffect, useCallback } from 'react';
import TimePicker from './TimePicker';

const TimeRangePicker = ({ value, onChange, onBlur, isValid, className, required = false, minHour = 0, maxHour = 24 }) => {
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');

    // Parse incoming value to set start and end times
    useEffect(() => {
        if (value && typeof value === 'string') {
            const match = value.match(/^(.+?)\s*-\s*(.+)$/);
            if (match) {
                const newStartTime = match[1].trim();
                const newEndTime = match[2].trim();

                // Only update state if the values are actually different
                // This prevents circular updates when user is actively editing
                if (newStartTime !== startTime || newEndTime !== endTime) {
                    setStartTime(newStartTime);
                    setEndTime(newEndTime);
                }
            } else {
                // If no dash found, treat as empty only if current values aren't empty
                if (startTime !== '' || endTime !== '') {
                    setStartTime('');
                    setEndTime('');
                }
            }
        } else {
            // Only clear if current values aren't already empty
            if (startTime !== '' || endTime !== '') {
                setStartTime('');
                setEndTime('');
            }
        }
    }, [value, startTime, endTime]);

    // Update parent when either time changes
    const updateTimeValue = useCallback((newStartTime, newEndTime) => {
        const timeString = `${newStartTime} - ${newEndTime}`;

        // Only call onChange if the combined value is actually different from current value
        if (timeString !== value) {
            onChange({ target: { value: timeString, name: 'time' } });
        }
    }, [onChange, value]);

    const handleStartTimeChange = useCallback((e) => {
        const newStartTime = e.target.value;

        // Only update if the value is actually different
        if (newStartTime !== startTime) {
            setStartTime(newStartTime);
            updateTimeValue(newStartTime, endTime);
        }
    }, [startTime, endTime, updateTimeValue]);

    const handleEndTimeChange = useCallback((e) => {
        const newEndTime = e.target.value;

        // Only update if the value is actually different
        if (newEndTime !== endTime) {
            setEndTime(newEndTime);
            updateTimeValue(startTime, newEndTime);
        }
    }, [startTime, endTime, updateTimeValue]);

    return (
        <div className={`space-y-2 ${className || ''}`}>
            {/* Start and End Time - Horizontal Layout */}
            <div className="flex gap-4 items-end">
                {/* Start Time */}
                <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                    <TimePicker
                        value={startTime}
                        onChange={handleStartTimeChange}
                        onBlur={onBlur}
                        isValid={isValid}
                        placeholder="09:00"
                        required={required}
                        minHour={minHour}
                        maxHour={maxHour}
                    />
                </div>

                {/* Visual separator */}
                <div className="flex-none pb-2 text-gray-400 font-medium">
                    —
                </div>

                {/* End Time */}
                <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                    <TimePicker
                        value={endTime}
                        onChange={handleEndTimeChange}
                        onBlur={onBlur}
                        isValid={isValid}
                        placeholder="10:30"
                        required={required}
                        minHour={minHour}
                        maxHour={maxHour}
                    />
                </div>
            </div>

            {/* Compact time range display */}
            {/* {startTime && endTime && (
                <div className="text-xs text-gray-500 text-center">
                    {`${startTime} - ${endTime}`}
                    {!isValid && (
                        <span className="text-red-500 ml-2">⚠️ Invalid range</span>
                    )}
                </div>
            )} */}
        </div>
    );
};

export default TimeRangePicker; 